// ----------------------------------------------------------------------------
// Snippet: Video Player
// ----------------------------------------------------------------------------
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Scripts
import Vimeo from '@vimeo/player';
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';

// Styles
import './_video-player__controls.scss';

// -------------------------------------
// Variables
// -------------------------------------

// Selectors
var VIDEO_BLOCK = '.video-player';
var VIDEO = '.video-player__video';
var VIDEO_MODAL = '.modal--video-modal';
var YOUTUBE_VIDEO = '.video-player--youtube';
var VIMEO_VIDEO = '.video-player--vimeo';
var HTML5_VIDEO = '.video-player--html5';
var CONTROLS_PLAY = '.video-player__control--play';
var CONTROLS_PAUSE = '.video-player__control--pause';

// Classes
var POSTERED_CLASS = 'video-player--is-postered';
var PAUSED_CLASS = 'video-player--is-paused';
var DYNAMIC_VIDEO_CLASS = 'video-player--dynamic-video';

// Events
var playerPlay = new Event('onplay.video-player');
var playerPause = new Event('onpause.video-player');
var play = new Event('play.video-player');
var pause = new Event('pause.video-player');

// -------------------------------------
// Helpers
// -------------------------------------

var videoBlocks = {
  init: function init() {
    var videoBlockElems = document.querySelectorAll(VIDEO_BLOCK);
    videoBlockElems.forEach(function (videoBlock) {
      videoBlocks.initVideoBlock(videoBlock);
    });
  },
  initVideoBlock: function initVideoBlock(videoBlock) {
    var videoElement = videoBlock.querySelector(VIDEO);
    if (!videoElement) return;
    videoBlock.playButton = videoBlock.querySelector(CONTROLS_PLAY);
    videoBlock.pauseButton = videoBlock.querySelector(CONTROLS_PAUSE);
    if (videoBlock.playButton !== null) {
      videoElement.setAttribute('inert', '');
    } else {
      videoElement.removeAttribute('inert');
    }
    videoBlock.addEventListener('onplay.video-player', function () {
      videoBlockControls.play(videoBlock);
    });
    videoBlock.addEventListener('onpause.video-player', function () {
      videoBlockControls.pause(videoBlock);
    });
    if (videoBlock.playButton !== null) {
      videoBlock.playButton.addEventListener('click', function () {
        videoBlocks.startPlayback(videoBlock, {
          focusPauseButton: true
        });
      });
    }
    if (videoBlock.pauseButton !== null) {
      videoBlock.pauseButton.addEventListener('click', function () {
        videoBlocks.stopPlayback(videoBlock, {
          focusPlayButton: true
        });
      });
    }
  },
  startPlayback: function startPlayback(videoBlock) {
    var {
      focusPauseButton = false
    } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var videoElement = videoBlock.querySelector(VIDEO);
    if (!videoElement) return;
    videoBlockControls.play(videoBlock);
    videoBlock.dispatchEvent(play);
    videoElement.removeAttribute('inert');
    if (focusPauseButton) {
      var _videoBlock$pauseButt;
      (_videoBlock$pauseButt = videoBlock.pauseButton) === null || _videoBlock$pauseButt === void 0 || _videoBlock$pauseButt.focus();
    }
  },
  stopPlayback: function stopPlayback(videoBlock) {
    var {
      focusPlayButton = false
    } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var videoElement = videoBlock.querySelector(VIDEO);
    if (!videoElement) return;
    videoBlockControls.pause(videoBlock);
    videoBlock.dispatchEvent(pause);
    if (videoBlock.playButton !== null) {
      videoElement.setAttribute('inert', '');
    }
    if (focusPlayButton) {
      var _videoBlock$playButto;
      (_videoBlock$playButto = videoBlock.playButton) === null || _videoBlock$playButto === void 0 || _videoBlock$playButto.focus();
    }
  }
};
var videoBlockControls = {
  play: function play(videoBlock) {
    videoBlock.classList.remove(POSTERED_CLASS);
    videoBlock.classList.remove(PAUSED_CLASS);
  },
  pause: function pause(videoBlock) {
    if (videoBlock.playButton !== null) {
      videoBlock.classList.add(POSTERED_CLASS);
      videoBlock.classList.add(PAUSED_CLASS);
    } else {
      videoBlock.classList.remove(POSTERED_CLASS);
      videoBlock.classList.remove(PAUSED_CLASS);
    }
  }
};
var modalVideos = {
  init: function init() {
    iamotaEventHandler.subscribe('modal:opened', modalVideos.handleModalOpened, {
      source: 'video-player'
    });
    iamotaEventHandler.subscribe('modal:closed', modalVideos.handleModalClosed, {
      source: 'video-player'
    });
  },
  isOpen: function isOpen(videoBlock) {
    var _videoBlock$closest;
    return (_videoBlock$closest = videoBlock.closest(VIDEO_MODAL)) === null || _videoBlock$closest === void 0 ? void 0 : _videoBlock$closest.classList.contains('modal--open');
  },
  handleModalOpened: function handleModalOpened(_ref) {
    var {
      modal
    } = _ref;
    if (!(modal !== null && modal !== void 0 && modal.matches(VIDEO_MODAL))) return;
    modal.querySelectorAll(VIDEO_BLOCK).forEach(videoBlock => {
      videoBlocks.startPlayback(videoBlock);
    });
  },
  handleModalClosed: function handleModalClosed(_ref2) {
    var {
      modal
    } = _ref2;
    if (!(modal !== null && modal !== void 0 && modal.matches(VIDEO_MODAL))) return;
    modal.querySelectorAll(VIDEO_BLOCK).forEach(videoBlock => {
      videoBlocks.stopPlayback(videoBlock);
    });
  }
};
var youtubeVideos = {
  init: function init() {
    var tag = document.createElement('script');
    var firstScriptTag = document.getElementsByTagName('script')[0];
    tag.src = 'https://www.youtube.com/iframe_api';
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    window.onYouTubeIframeAPIReady = function () {
      youtubeVideos.initVideos();
    };
  },
  initVideos: function initVideos() {
    var videoBlocks = document.querySelectorAll(YOUTUBE_VIDEO);
    videoBlocks.forEach(function (videoBlock) {
      var video = youtubeVideos.getVideo(videoBlock);
      var videoIframe = document.createElement('div');
      var videoSettings = JSON.parse(video.dataset.videoSettings);
      var videoID = videoSettings.video_id;
      video.appendChild(videoIframe);
      new window.YT.Player(videoIframe, {
        videoId: videoID,
        playerVars: {
          playlist: videoSettings.playlist_id == null && videoSettings.is_looped ? videoID : null,
          loop: videoSettings.is_looped ? 1 : 0,
          autoplay: videoSettings.is_autoplay ? 1 : 0,
          controls: videoSettings.is_controls ? 1 : 0,
          disablekb: videoSettings.is_controls ? 0 : 1,
          playsinline: videoSettings.is_autoplay ? 1 : 0,
          showinfo: 0,
          modestbranding: 1,
          fs: 1,
          cc_load_policy: 0,
          iv_load_policy: 3,
          rel: 0
        },
        events: {
          'onReady': youtubeVideos.onPlayerReady
        }
      });
    });
  },
  onPlayerReady: function onPlayerReady(event) {
    var player = event.target;
    var video = youtubeVideos.getVideo(player.getIframe().closest(VIDEO_BLOCK));
    var videoBlock = video.closest(VIDEO_BLOCK);
    var videoSettings = video.dataset.videoSettings;
    player.setVolume(videoSettings.is_muted ? 0 : 100);
    videoBlock.addEventListener('play.video-player', function () {
      youtubeVideos.play(player);
    });
    videoBlock.addEventListener('pause.video-player', function () {
      youtubeVideos.pause(player);
    });
    player.getIframe.tabIndex = -1;
    if (modalVideos.isOpen(videoBlock)) {
      videoBlocks.startPlayback(videoBlock);
    }
  },
  play: function play(player) {
    var iframe = player.getIframe();
    iframe.tabIndex = 0;
    player.playVideo();
  },
  pause: function pause(player) {
    player.pauseVideo();
  },
  getVideo: function getVideo(videoBlock) {
    return videoBlock.querySelector(VIDEO);
  }
};
var vimeoVideos = {
  init: function init() {
    var videoBlocks = document.querySelectorAll(VIMEO_VIDEO);
    videoBlocks.forEach(function (videoBlock) {
      var video = videoBlock.querySelector(VIDEO);
      var videoSettings = JSON.parse(video.dataset.videoSettings);
      var videoID = videoSettings.video_id;
      if (videoSettings.lookup_poster) {
        var url = 'https://vimeo.com/api/v2/video/' + videoID + '.json';
        fetch(url).then(response => response.json()).then(data => {
          videoBlock.classList.add(POSTERED_CLASS);
          videoBlock.style.backgroundImage = 'linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(' + data[0].thumbnail_large + ')';
        }).catch(() => {
          // error can be passed in as a parameter to be used
          videoBlock.classList.remove(POSTERED_CLASS);
          videoBlock.style.backgroundImage = '';
        });
      }
      var player = new Vimeo(video, {
        id: videoID,
        loop: videoSettings.is_looped,
        autoplay: videoSettings.is_autoplay,
        autopause: !videoSettings.is_autoplay,
        title: false,
        portrait: false,
        byline: false,
        controls: videoSettings.is_controls
      });
      player.setVolume(videoSettings.is_muted ? 0 : 1);
      videoBlock.addEventListener('play.video-player', function () {
        vimeoVideos.play(player);
      });
      videoBlock.addEventListener('pause.video-player', function () {
        vimeoVideos.pause(player);
      });
      if (modalVideos.isOpen(videoBlock)) {
        videoBlocks.startPlayback(videoBlock);
      }
    });
  },
  play: function play(player) {
    player.play();
  },
  pause: function pause(player) {
    player.pause();
  }
};
var html5Videos = {
  init: function init() {
    var videoBlocks = document.querySelectorAll(HTML5_VIDEO);
    videoBlocks.forEach(function (videoBlock) {
      var video = videoBlock.querySelector(VIDEO);

      // If this video block has dynamic videos on mobile and desktop, get the appropriate video sources on mobile/desktop
      if (videoBlock.classList.contains(DYNAMIC_VIDEO_CLASS)) {
        var videoSources = JSON.parse(video.dataset.videoSources);
        var desktopVideoUrls = videoSources.desktop_video_source_urls.split(',');
        var desktopVideoSourceTypes = videoSources.desktop_video_source_types.split(',');
        var mobileVideoUrls = videoSources.mobile_video_source_urls.split(',');
        var mobileVideoSourceTypes = videoSources.mobile_video_source_types.split(',');
        var sourceUrls;
        var sourceTypes;
        if (window.innerWidth > 680 || mobileVideoUrls[0] == '') {
          // Load desktop videos. Fallback to Desktop videos if there is no mobile video uploaded
          sourceUrls = desktopVideoUrls;
          sourceTypes = desktopVideoSourceTypes;
        } else {
          // Load mobile videos
          sourceUrls = mobileVideoUrls;
          sourceTypes = mobileVideoSourceTypes;
        }

        // Loop through all the source urls and types then insert the sources to the video element
        for (var index = 0; index < sourceUrls.length; index++) {
          video.insertAdjacentHTML('beforeend', "<source type='".concat(sourceTypes[index], "' src='").concat(sourceUrls[index], "'/>"));
        }
      }
      videoBlock.addEventListener('play.video-player', function () {
        video.play();
      });
      videoBlock.addEventListener('pause.video-player', function () {
        video.pause();
      });
      video.onplay = function () {
        videoBlock.dispatchEvent(playerPlay);
      };
      video.onpause = function () {
        videoBlock.dispatchEvent(playerPause);
      };
      if (modalVideos.isOpen(videoBlock)) {
        videoBlocks.startPlayback(videoBlock);
      }
    });
  }
};

// -------------------------------------
// Hooks
// -------------------------------------

document.addEventListener('DOMContentLoaded', function () {
  videoBlocks.init();
  youtubeVideos.init();
  vimeoVideos.init();
  html5Videos.init();
  modalVideos.init();
});